import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Loader,
  Header,
  Button,
  Label,
  Input,
  FieldGroup,
  Checkbox,
  TextArea,
  PetPhoto,
  Fields,
} from '@pet-notify/ui'

import { usePetByIntellitag } from 'hooks/pets'
import { useCreate } from 'hooks/found-pets'
import useDocumentTitle from 'hooks/document-title'
import ErrorMessage from 'components/ErrorMessage'
import { API_URL } from 'config'

const schema = yup.object().shape({
  name: yup.string().required(),
  phone: yup.string().required(),
})

type FormData = {
  name: string
  email: string
  phone: string
  hasAlternateContact: boolean
  alternateName?: string
  alternatePhone?: string
  alternateEmail?: string
  pickupInstructions: string
}

function DetailsPage() {
  useDocumentTitle('How do we reach you? | Pet Notify')
  const { number } = useParams()
  const petQuery = usePetByIntellitag(number)
  const navigate = useNavigate()
  const createMutation = useCreate()
  const { register, handleSubmit, watch } = useForm<FormData>({
    resolver: yupResolver(schema),
  })
  const [petLocation, setPetLocation] = useState<{latitude: number, longitude: number}>()

  useEffect(() => {
    if (petQuery.isError) {
      navigate('/found')
    }
  }, [petQuery])

  const pet = petQuery.data

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          // console.log(`Latitude: ${latitude}, Longitude: ${longitude}, Timestamp: ${position.timestamp}`);
          
          const location = {
            latitude,
            longitude,
            timestamp: position.timestamp,
          };
          setPetLocation(location)
        },
        (error) => {
          console.error(`Error Code = ${error.code} - ${error.message}`);
        }
      );
    } else {
      // console.log("Geolocation is not supported by this browser.");
    }    
  }, [])  

  useEffect(() => {
    if(petLocation?.latitude && petLocation?.longitude) {
      updatePetLocation(pet.id, petLocation)
    }
  }, [petLocation])

  async function updatePetLocation(petId, location) {
    // console.log(`Update pet location API started`)
    const url = `${API_URL}/api/pets/${petId}/location`;
  
    const response = await fetch(url, {
      method: 'PATCH', // Use PATCH instead of PUT
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ location }),
    });
  
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error updating location:', errorData);
      throw new Error(errorData.error || 'Error updating location');
    }
  
    const responseData = await response.json();
    // console.log('Location updated successfully:', responseData);
    return responseData;
  }

  if (!petQuery.data || petQuery.isLoading) {
    return <Loader />
  }

  const onSubmit = handleSubmit((values) => {
    const updatedValues = {
      ...values,
      latitude: petLocation?.latitude,
      longitude: petLocation?.longitude,
    }
    if (number) {
      createMutation.mutate(
        { ...updatedValues, number },
        {
          onSuccess: (response) => {
            navigate(`/found/confirmation/${response.token}`)
          },
        },
      )
    }
  })

  return (
    <>
      {pet.photo && <PetPhoto src={pet.photo.sizes.large.src} alt={pet.name} />}

      <Header as='h1' size='xlarge' mb='6'>
        You found {pet.name}! Tell us how to reach you and we&apos;ll connect
        you with the pet parent right away.
      </Header>

      <Header as='h2' size='large' mb='6'>
        Tell us how to reach you
      </Header>

      <form onSubmit={onSubmit}>
        <Fields>
          <FieldGroup>
            <Label htmlFor='name'>Full name</Label>
            <Input id='name' type='text' {...register('name')} />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor='phone'>Phone</Label>
            <Input id='phone' type='phone' {...register('phone')} />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor='email'>Email</Label>
            <Input
              id='email'
              placeholder='Optional'
              type='email'
              {...register('email')}
            />
          </FieldGroup>

          <FieldGroup>
            <Checkbox
              label='Add an alternate contact'
              id='hasAlternateContact'
              {...register('hasAlternateContact')}
            />
          </FieldGroup>

          {watch('hasAlternateContact') && (
            <>
              <FieldGroup>
                <Label htmlFor='alternateName'>Alternate full name</Label>
                <Input
                  id='alternateName'
                  type='text'
                  {...register('alternateName')}
                />
              </FieldGroup>

              <FieldGroup>
                <Label htmlFor='alternatePhone'>Alternate phone</Label>
                <Input id='alternatePhone' {...register('alternatePhone')} />
              </FieldGroup>

              <FieldGroup>
                <Label htmlFor='alternateEmail'>Alternate email</Label>
                <Input
                  id='alternateEmail'
                  placeholder='Optional'
                  type='email'
                  {...register('alternateEmail')}
                />
              </FieldGroup>
            </>
          )}
        </Fields>

        <Header as='h2' size='large' mb='6'>
          Where can the parent pick up their pet?
        </Header>
        <Header as='h2' size='medium' mb='6'>
          Please allow location permissions to provide pet location to its parent.
        </Header>

        <Fields>
          <FieldGroup>
            <Label htmlFor='pickupInstructions'>Other pickup Instructions (optional)</Label>
            <TextArea
              id='pickupInstructions'
              {...register('pickupInstructions')}
            />
          </FieldGroup>

          {createMutation.isError && (
            <ErrorMessage error={createMutation.error} />
          )}
        </Fields>

        <Button type='submit' disabled={createMutation.isLoading}>
          Notify the pet owner
        </Button>
      </form>
    </>
  )
}
export default DetailsPage
